import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { Camera, Sparkles, HeartPulse, ArrowRight } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1551184451-76b762941ad6?auto=format&fit=crop&w=900&q=80";

export default function Landing() {
  const { user } = useAuth();
  const startTo = user ? "/create" : "/login";

  return (
    <div className="relative min-h-screen">
      <Navbar />
      <div
        className="pointer-events-none absolute top-0 right-0 w-[60vw] h-[60vw] max-w-[700px] max-h-[700px] rounded-full blur-3xl opacity-40"
        style={{ background: "radial-gradient(circle, #F4C9B4 0%, transparent 70%)" }}
      />

      <main className="relative max-w-6xl mx-auto px-6 pt-16 sm:pt-24 pb-24">
        <section className="grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            className="lg:col-span-6"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase font-bold text-sage mb-6">
              <Sparkles className="w-4 h-4" /> AI Future-Self Portrait
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-charcoal leading-[1.05]">
              Meet the you of <span className="italic" style={{ color: "var(--primary)" }}>2046</span>.
            </h1>
            <p className="text-base sm:text-lg text-muted-c mt-6 max-w-md leading-relaxed">
              Take a selfie, answer a few habit questions, and our AI reveals exactly how you'll
              look in 20 years — plus a health score and glow-up tips to change the ending.
            </p>
            <div className="mt-9 flex items-center gap-5">
              <Link
                to={startTo}
                data-testid="hero-start-btn"
                className="btn-primary inline-flex items-center gap-2 px-8 py-4 text-base font-semibold"
              >
                Start Your Glow-Up <ArrowRight className="w-4 h-4" />
              </Link>
              <span className="text-sm text-muted-c">One reveal · $7</span>
            </div>
          </motion.div>

          <motion.div
            className="lg:col-span-6 relative"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.15 }}
          >
            <div className="relative rounded-3xl overflow-hidden soft-shadow border border-black/5">
              <img src={HERO} alt="Future self glow" className="w-full h-[440px] object-cover" />
              <div className="absolute bottom-4 left-4 glass rounded-2xl px-4 py-3 border border-white/40">
                <div className="text-xs tracking-[0.15em] uppercase font-bold text-sage">Health Score</div>
                <div className="font-heading text-3xl text-charcoal">87</div>
              </div>
            </div>
          </motion.div>
        </section>

        <section className="grid md:grid-cols-3 gap-6 mt-24">
          {[
            { icon: Camera, t: "Snap a selfie", d: "Use your camera or upload a clear front-facing photo." },
            { icon: HeartPulse, t: "Share your habits", d: "Sleep, sun, diet, smoking, water & stress — quick taps." },
            { icon: Sparkles, t: "See your future", d: "An AI-aged portrait, health score and personal glow-up tips." },
          ].map((s, i) => (
            <motion.div
              key={s.t}
              className="bg-white rounded-3xl p-8 soft-shadow border border-black/5"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
            >
              <s.icon className="w-7 h-7 mb-5" style={{ color: "var(--primary)" }} />
              <h3 className="font-heading text-xl text-charcoal mb-2">{s.t}</h3>
              <p className="text-sm text-muted-c leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </section>
      </main>
    </div>
  );
}
